/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContent.js
 * @brief  This file is responsible for general tab functionality
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useRef, useId } from "react";
import FilterCategory from "./FilterCategory";
import FilterLocationSearch from "../FilterLocationSearch";
import FilterKeywordSearch from "../FilterKeywordSearch";
import Accordion from "react-bootstrap/Accordion";
import { isMlsRestrictedTenant } from "@/helpers/tenant";
import { useAppConfig } from "@/appConfig";
import { filterContext } from "../../../../pages/v2/Home";
import {
  accsPvtBthRmchkBxFilter,
  adaAccessibleChkBx,
  alwPetsChkBx,
  BUY,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
  DEFAULT_STEP_VALUE_BUY,
  DEFAULT_STEP_VALUE_LTR,
  DEFAULT_STEP_VALUE_STR,
  INDEX_TO_BE_SLICE_15,
  LONG_RENT,
  pvtEntrnceChkBx,
} from "../../../../constants/common";
import {
  getPropertyTypes,
  getTimeline,
} from "../../../../services/commonServices";
import {
  getSquareFeetList,
  getFinanceType,
  getPurchaseTypes,
} from "../../../../services/wantAdService";
import { handleRadioButonChange } from "../../../../actions/common";
import RangeSliderComponent from "../../../Common/RangeSlider";
import SelectDropDown from "../../../Common/SelectDropDown";
import {
  convertToNumber,
  handleNegativeInput,
} from "../../../../helpers/validations";
import TextBox from "../../../Common/TextBox";
import {
  replaceComma,
  getLocalStorage,
  replaceString,
  thousandSeperatedValue,
  thousandSeperatedValueFilter,
  getPriceText,
  checkIsNumber,
  arraySplice,
  filterArrayBasedOnKey,
} from "../../../../helpers/common";
import DateRangeSelector from "../../../DateRangeSelector";
import PremiumPlanModal from "../../../../components/PlanUpgrade/PremiumPlanModal";
import { useUserDetails } from "@/hooks";
import { formatThousandSeparatedValue } from "@/helpers";
import { computeActiveFilterCount } from "@/helpers/activeFilterCount";


const LeftpanelTabContent = (props) => {
  const {
    updateFilter,
    filterQuery,
    setUserLocation,
    gitchaNotAvailble,
    checkGitchaAvailableInLoc,
    userLocation,
    additionalLocations,
    removeLocationAt,
  } = props;

  const {
    userDetails: userDetailsFromHook,
    loadUserData,
  } = useUserDetails();
  const { tenant } = useAppConfig() || {};

  // This component mounts more than once at a time (the always-mounted
  // desktop rail's Buyers-tab content, plus the Sort and Filter popup),
  // each bound to its own filterQuery/updateFilter. Checkbox ids below were
  // raw values (e.g. "1") shared across every mount, so a real click on a
  // <label> - not the tiny checkbox square itself - resolved via the
  // browser's native htmlFor -> getElementById and hit whichever instance
  // happened to be first in the DOM, silently toggling the wrong (often
  // hidden) instance's state instead of the one the user was looking at.
  const instanceId = useId();

  // Selected cities render as removable chips (see FilterLocationSearch),
  // so the input itself always starts empty/typing-only rather than
  // mirroring the primary location's formatted address.
  const [location, setLocation] = useState("");
  const [propertyList, setPropertyList] = useState([]);
  const [financeList, setFinanceList] = useState([]);
  const [timelineList, setTimelineList] = useState([]);
  const [purchaseList, setPurchaseList] = useState([]);
  // Financing/Timeline/Purchase-type default open rather than behind an
  // extra click - they're as core to filtering the feed as anything above.
  const [showMore, setShowMore] = useState(true);
  const [typeOfPlaceList, setTypeOfPlaceList] = useState([]);
  const [selectedTypeOfPlace, setSelectedTypeOfPlace] = useState([]);
  const [budgetRange, setBudgetRange] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [minError, setMinError] = useState("");
  const [maxError, setMaxError] = useState("");

  const getSelectedItems = (property) => {
    const selectedItemArray = _.get(filterQuery, property, []);
    return selectedItemArray;
  };

  // Number of filters actually narrowing the results. Shown on the single
  // "Filters" control so an agent can see their feed is filtered without
  // opening anything - the pattern Airtasker and Zillow both use.
  const activeFilterCount = React.useMemo(
    () => computeActiveFilterCount(filterQuery),
    [filterQuery],
  );

  const [searchKeyword, setSearchKeyword] = useState(filterQuery?.search || "");
  const [squareFootageMinValue, setSquareFootageMinValue] = useState(null);
  const [squareFootageMaxValue, setSquareFootageMaxValue] = useState(null);
  const filterCntxt = useContext(filterContext);
  const [propertyCount, setPropertyCount] = useState(filterQuery?.propertyType?.length || 0);
  const [typeOfPlaceCount, setTPCount] = useState(0);
  const { adType, clearFilterQuery } = filterCntxt;
  const [isPrivateRoomSelected, setIsPrivateRoomSelected] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [squareFeetMinList, setSqareFeetMinList] = useState([]);
  const [squareFeetMaxList, setSqareFeetMaxList] = useState([]);
  const [isAgentOnly, setIsAgentOnly] = useState(false);
  const isFirstMount = useRef(true);
  const isFirstSqftLoad = useRef(true);
  const isFirstAdType = useRef(true);

  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const MIN_SF_DROPDOWN = "minSFDrop";
  const MAX_SF_DROPDOWN = "maxSFDrop";
  const minRef = useRef(null);
  const maxRef = useRef(null);
  let DEFAULT_MAX_BUDGET;
  const DEFAULT_MIN_BUDGET = 0;
  let DEFAULT_STEP_VALUE;

  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_STR;
  }

  const budgetMin = _.get(filterQuery, "priceMin");
  const budgetMax = _.get(filterQuery, "priceMax");
  const STR_OPTION_DETAILS_PVT_ROOM = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
    { id: accsPvtBthRmchkBxFilter, text: "Access to private bathroom" },
    { id: pvtEntrnceChkBx, text: "Private Entrance" },
  ];

  const STR_OPTION_DETAILS_ALL = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
  ];

  const formatSqFtLabel = (val, originalLabel = "") => {
    if (val === null || val === undefined) return originalLabel;
    const hasPlus = String(originalLabel).includes("+");
    return formatThousandSeparatedValue(Number(val)) + (hasPlus ? "+" : "");
  };

  useEffect(() => {
    setIsAgentOnly(false);
  }, [adType]);

  useEffect(() => {
    if (isFirstAdType.current) {
      isFirstAdType.current = false;
      return;
    }
    updateFilter({ timeline: [], purchaseType: [] });
  }, [adType]);

  useEffect(() => {
    getFinanceType()
      .then((response) => {
        if (response?.errorCode === 0) {
          setFinanceList(_.get(response, "result", []) || []);
        }
      })
      .catch((err) => console.log("Error", err));
    getPurchaseTypes()
      .then((response) => {
        if (response?.errorCode === 0) {
          setPurchaseList(_.get(response, "result", []) || []);
        }
      })
      .catch((err) => console.log("Error", err));
  }, []);

  useEffect(() => {
    getTimeline(adType ?? BUY)
      .then((response) => {
        if (response?.errorCode === 0) {
          setTimelineList(_.get(response, "result", []) || []);
        }
      })
      .catch((err) => console.log("Error", err));
  }, [adType]);

  useEffect(() => {
    if (adType === BUY) {
      getPropertyTypes(adType)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      setPropertyList([]);
    }

    let DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET];
    if (budgetMin && !budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, DEFAULT_MAX_BUDGET];
    } else if (!budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, budgetMax];
    } else if (budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, budgetMax];
    }
    setBudgetRange(DEFAULT_BUDGET_RANGE);
    setSelectedTypeOfPlace([10, 11, 6]);

    getSquareFeetList().then(({ errorCode, result }) => {
      if (errorCode !== 0 || !result) return;

      const baseList = result.map((itm) => ({
        value: itm.Sqft_Value,
        label: formatSqFtLabel(itm.Sqft_Value, itm.Sqft_Value_Text),
      }));

      setSqareFeetMinList([{ value: null, label: "No min" }, ...baseList]);
      setSqareFeetMaxList([{ value: null, label: "No max" }, ...baseList]);

      // Restore square footage values from filter on first load
      if (isFirstSqftLoad.current && filterQuery?.sqrftMin != null) {
        setSquareFootageMinValue({
          value: filterQuery.sqrftMin,
          label: formatSqFtLabel(filterQuery.sqrftMin),
        });
      } else {
        setSquareFootageMinValue({ value: null, label: "No min" });
      }
      if (isFirstSqftLoad.current && filterQuery?.sqrftMax != null) {
        setSquareFootageMaxValue({
          value: filterQuery.sqrftMax,
          label: formatSqFtLabel(filterQuery.sqrftMax),
        });
      } else {
        setSquareFootageMaxValue({ value: null, label: "No max" });
      }
      isFirstSqftLoad.current = false;

      setStartDate(null);
      setEndDate(null);
    });
  }, [adType]);

  useEffect(() => {
    if (adType !== BUY) {
      let type = "";
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10)
      ) {
        type = "es-pr";
      } else if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "pr-land";
      } else if (
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "es-land";
      }
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "all";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(11)) {
        type = "privateroom";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(10)) {
        type = "entirespace";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(6)) {
        type = "land";
      } else {
        setPropertyList([]);
      }
      getPropertyTypes(type)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });

      if (selectedTypeOfPlace.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }
    }
  }, [selectedTypeOfPlace]);

  // Ref to track latest slider value for onThumbDragEnd
  const budgetRangeRef = useRef(budgetRange);

  const handleSliderInput = (value) => {
    setBudgetRange(value);
    budgetRangeRef.current = value;
  };

  // Only trigger filter when user releases the slider thumb, with a small delay
  const sliderDebounceRef = useRef(null);
  const handleSliderDragEnd = () => {
    if (sliderDebounceRef.current) clearTimeout(sliderDebounceRef.current);
    sliderDebounceRef.current = setTimeout(() => {
      const range = budgetRangeRef.current;
      if (!range) return;
      const maxVal = range[1] === DEFAULT_MAX_BUDGET ? "" : range[1];
      updateFilter({ priceMin: range[0], priceMax: maxVal });
    }, 200);
  };

  useEffect(() => {
    if (adType !== BUY) {
      ///      For loading type of place
      getPropertyTypes("rent")
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setTypeOfPlaceList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [adType]);
  /**
   * To handle clear filter
   */
  const clearFilter = () => {
    // On a BLS tenant the market is not a filter - it is the feed's context,
    // and the tenant owns it. Clearing it here only bounced the agent through
    // an empty state before the tenant default re-seeded. Consumer Gitcha
    // still chooses its location first, so it keeps the original behaviour.
    if (!isMlsRestrictedTenant(tenant)) {
      setUserLocation(null);
      setLocation("");
    }
    setSearchKeyword("");
    clearFilterQuery();
    setSquareFootageMinValue({ value: null, label: "No min" });
    setSquareFootageMaxValue({ value: null, label: "No max" });
    setBudgetRange([DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET]);
    setPropertyCount(0);
    setTPCount(0);
  };

  /**
   * To handle property selection
   * @param {*} e
   */
  const handlePropertyClick = (e) => {
    const value = convertToNumber(handleRadioButonChange(e));
    const selectedItemArray = getSelectedItems("propertyType");
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      updateFilter({ propertyType: itemArray });
      setPropertyCount(itemArray.length);
    }
  };

  const toggleMultiSelect = (filterKey, e) => {
    const value = convertToNumber(handleRadioButonChange(e));
    if (!value) return;
    const selected = getSelectedItems(filterKey);
    const itemArray = _.includes(selected, value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    updateFilter({ [filterKey]: itemArray });
  };

  const handleFinanceClick = (e) => toggleMultiSelect("financeType", e);
  const handleTimelineClick = (e) => toggleMultiSelect("timeline", e);
  const handlePurchaseClick = (e) => toggleMultiSelect("purchaseType", e);

  const handlePreApprovedClick = (e) => {
    updateFilter({ preApproved: e.target.checked });
  };

  const loadCheckboxList = (list, idKey, textKey, filterKey, handler) => {
    const selected = getSelectedItems(filterKey);
    return (
      list &&
      list.map((item) => {
        const id = _.get(item, idKey, "");
        const text = _.get(item, textKey, "");
        return (
          <label
            key={`${filterKey}-${id}`}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={`${instanceId}-${filterKey}-${id}`}
          >
            <input
              className="form-check-input"
              type="checkbox"
              value={id ? id : ""}
              id={`${instanceId}-${filterKey}-${id}`}
              checked={_.includes(selected, id)}
              onChange={handler}
            />
            {text}
          </label>
        );
      })
    );
  };

  const loadFinance = () =>
    loadCheckboxList(
      financeList,
      "FinanceTypeId",
      "FinanceTypeText",
      "financeType",
      handleFinanceClick,
    );

  const loadTimeline = () =>
    loadCheckboxList(
      timelineList,
      "TimeLineId",
      "TimeLineText",
      "timeline",
      handleTimelineClick,
    );

  const loadPurchase = () =>
    loadCheckboxList(
      purchaseList,
      "PurchaseTypeId",
      "PurchaseTypeText",
      "purchaseType",
      handlePurchaseClick,
    );

  const handleAdditionalRequirementsClick = (e, typeId) => {
    const selectedItemArray = getSelectedItems(e.target.value);
    if (selectedItemArray == 0 || selectedItemArray == null) {
      updateFilter({ [typeId]: 1 });
    } else {
      updateFilter({ [typeId]: 0 });
    }
  };
  /**
   * To handle type of place selection
   * @param {*} e
   */
  const handleTypeOfPlaceClick = (e) => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const value = convertToNumber(handleRadioButonChange(e));
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      setSelectedTypeOfPlace(itemArray);
      if (itemArray.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }

      updateFilter({ typeOfPlace: itemArray });
      setTPCount(itemArray.length);
    }
  };
  /**
   * To load property types
   * @returns
   */
  const loadPropertyMain = () => {
    const selectedItemArray = getSelectedItems("propertyType");
    const List =
      propertyList &&
      propertyList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={`${instanceId}-${typeId}`}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={`${instanceId}-${typeId}`}
              checked={isChecked}
              onChange={(e) => handlePropertyClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };
  /**
   * To load additional requirements section
   * @returns
   */
  const loadAdditionalRequirements = () => {
    const options = isPrivateRoomSelected
      ? STR_OPTION_DETAILS_PVT_ROOM
      : STR_OPTION_DETAILS_ALL;

    const List =
      options &&
      options.map((pt) => {
        const typeId = _.get(pt, "id", "");
        const typeText = _.get(pt, "text", "");
        const isChecked = getSelectedItems(typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={`${instanceId}-${typeId}`}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={`${instanceId}-${typeId}`}
              checked={isChecked == 1 ? true : false}
              onChange={(e) => handleAdditionalRequirementsClick(e, typeId)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  /**
   * To load types of place view
   * @returns
   */
  const loadTypeOfPlace = () => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const List =
      typeOfPlaceList &&
      typeOfPlaceList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={`${instanceId}-${typeId}`}
          >
            <input
              className="form-check-input"
              name="chkPlace"
              type="checkbox"
              value={typeId ? typeId : ""}
              id={`${instanceId}-${typeId}`}
              checked={isChecked}
              onChange={(e) => handleTypeOfPlaceClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  // The panel this renders inside unmounts on close (sortFilterOpen toggle,
  // Offcanvas), and closing right after typing is the natural gesture now
  // that "Show results" is gone. clearTimeout on unmount would otherwise
  // drop the keystrokes silently, so the pending value is flushed instead -
  // via refs, since a same-render close/reopen shouldn't refire this from a
  // stale closure over the mount-time searchKeyword/updateFilter.
  const latestSearchKeywordRef = useRef(searchKeyword);
  const updateFilterRef = useRef(updateFilter);
  // True only while a debounce timer is actually in flight, so a panel
  // open/close with no typing (or one where the debounce already fired)
  // doesn't flush a redundant, identical updateFilter call on unmount.
  const pendingSearchFlushRef = useRef(false);
  useEffect(() => {
    latestSearchKeywordRef.current = searchKeyword;
    updateFilterRef.current = updateFilter;
  });

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    pendingSearchFlushRef.current = true;
    const timer = setTimeout(() => {
      pendingSearchFlushRef.current = false;
      updateFilter({ search: searchKeyword });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    return () => {
      if (pendingSearchFlushRef.current) {
        pendingSearchFlushRef.current = false;
        updateFilterRef.current({ search: latestSearchKeywordRef.current });
      }
    };
  }, []);

  const handleKeyWordSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleBudgetMinChange = (e) => {
    const budgetMax = budgetRange && budgetRange[1];
    const value = handleNegativeInput(e.target.value);
    if (value) {
      setBudgetRange([parseInt(replaceComma(value)), budgetMax]);
    } else {
      setBudgetRange([null, budgetMax]);
    }
  };

  const handleBudgetMaxChange = (e) => {
    const budgetMin = budgetRange && budgetRange[0];
    const value = handleNegativeInput(e.target.value);
    if (parseInt(replaceComma(value))) {
      setBudgetRange([budgetMin, parseInt(replaceComma(value))]);
    } else {
      setBudgetRange([budgetMin, null]);
    }
  };

  // Trigger filter when user finishes editing budget text inputs
  const handleBudgetBlur = () => {
    if (!budgetRange) return;
    const maxVal = budgetRange[1] === DEFAULT_MAX_BUDGET ? "" : budgetRange[1];
    updateFilter({ priceMin: budgetRange[0], priceMax: maxVal });
  };

  const handleSquareFootMinChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMinValue(e);
    updateFilter({ sqrftMin: value });
  };

  const handleSquareFootMaxChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMaxValue(e);
    updateFilter({ sqrftMax: value });
  };

  /**
   * To handle number of bedrooms selection
   * @returns
   */
  const handleBedRoomClick = (e) => {
    const noOfBedrooms = getSelectedItems("bedrooms");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBedrooms) {
      updateFilter({ bedrooms: null });
    } else {
      updateFilter({ bedrooms: selectedValue });
    }
  };
  /**
   * To handle number of beds selection
   * @returns
   */
  const handleBedsClick = (e) => {
    const noOfBeds = getSelectedItems("beds");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBeds) {
      updateFilter({ beds: null });
    } else {
      updateFilter({ beds: selectedValue });
    }
  };
  /**
   * To handle number of bathrooms selection
   * @returns
   */
  const handleBathRoomClick = (e) => {
    const noOfBathrooms = getSelectedItems("baths");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBathrooms) {
      updateFilter({ baths: null });
    } else {
      updateFilter({ baths: selectedValue });
    }
  };

  const addNewOptionsToMinList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMinList(customList);
  };

  const addNewOptionsToMaxList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMaxList(customList);
  };

  const buildCustomList = (list, newOption) => {
    const parseNumeric = (v) =>
      v === null || v === undefined
        ? null
        : Number(String(v).replace(/[^\d.-]/g, ""));

    const formattedList = list.map((item) => {
      if (item.value === null) return item;
      return {
        value: item.value,
        label: formatSqFtLabel(item.value, item.label),
      };
    });

    const value = parseNumeric(_.get(newOption, "value", null));
    if (value === null) return formattedList;

    const exists = formattedList.some(
      (itm) => parseNumeric(itm.value) === value,
    );
    if (!exists) {
      formattedList.push({
        value,
        label: formatSqFtLabel(value),
      });
    }
    return formattedList;
  };

  const handleOnInputChange = (e, dropSection) => {
    if (dropSection === MIN_SF_DROPDOWN) {
      if (minRef.current && minRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMinList(
            arraySplice(squareFeetMinList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMinValue(options);
          updateFilter({ sqrftMin: e });
        }
      }
    } else {
      if (maxRef.current && maxRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMaxList(
            arraySplice(squareFeetMaxList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMaxValue(options);
          updateFilter({ sqrftMax: e });
        }
      }
    }
  };

  /**
   * To handle number of bedrooms selection view
   * @returns
   */
  const loadBedRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("bedrooms");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of beds selection view
   * @returns
   */
  const loadBeds = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("beds");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedsClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of bathrooms selection view
   * @returns
   */
  const loadBathRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBathrooms = getSelectedItems("baths");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBathrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBathRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };

  const updateReloadParamAfterPlanChange = () => {
    loadUserData();
  };

  const moreFilterCount =
    getSelectedItems("financeType").length +
    (filterQuery?.preApproved ? 1 : 0) +
    getSelectedItems("timeline").length +
    (adType === BUY ? getSelectedItems("purchaseType").length : 0);

  return (
    <>

      {props?.filtersOnly ? null : (
        <label className="feed-field-label" htmlFor={`feed-location-search-${instanceId}`}>
          City, ZIP, address, or area
        </label>
      )}
      {props?.filtersOnly ? null : (
      <FilterLocationSearch
        id={`feed-location-search-${instanceId}`}
        location={location}
        setLocation={setLocation}
        updateFilter={updateFilter}
        adType={adType}
        gitchaNotAvailble={gitchaNotAvailble}
        checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
        additionalLocations={additionalLocations}
        removeLocationAt={removeLocationAt}
      />
      )}
      {props?.locationOnly ? null : (
        <>
          <label className="feed-field-label" htmlFor={`feed-keyword-search-${instanceId}`}>
            Search keywords, agents, or brokerages
          </label>
          <FilterKeywordSearch
            id={`feed-keyword-search-${instanceId}`}
            handleSearch={handleKeyWordSearch}
            keyWord={searchKeyword}
          />
        </>
      )}

      <div
        className="feed-filters-toggle"
        hidden={props?.inDrawer === false || props?.filtersOnly || props?.locationOnly}
      >
        <button
          type="button"
          className={`feed-filters-btn ${filtersOpen ? "is-open" : ""}`}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <span>
            Filters
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </span>
          <i className="feed-filters-caret" aria-hidden="true" />
        </button>
      </div>

      <div hidden={props?.locationOnly ? true : props?.filtersOnly ? false : !filtersOpen || props?.inDrawer === false}>
      <Accordion
        defaultActiveKey={
          props?.collapsedSections
            ? null
            : props?.filtersOnly
              ? ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
              : gitchaNotAvailble
                ? null
                : "2"
        }
        alwaysOpen={!props?.collapsedSections}
        className="mt-2"
      >
        {adType !== BUY && (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Type of place{" "}
              {getSelectedItems("typeOfPlace").length > 0
                ? `(${getSelectedItems("typeOfPlace").length})`
                : ""}
            </Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">{loadTypeOfPlace()}</div>
            </Accordion.Body>
          </Accordion.Item>
        )}

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            Property type{" "}
            {getSelectedItems("propertyType").length > 0
              ? `(${getSelectedItems("propertyType").length})`
              : ""}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="field-wrap mt-0 mb-0">{loadPropertyMain()}</div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>{getPriceText(adType)}</Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="slider-sec">
              <div className="pb-4 pt-3">
                <RangeSliderComponent
                  min={DEFAULT_MIN_BUDGET}
                  max={DEFAULT_MAX_BUDGET}
                  value={
                    budgetRange && [
                      budgetRange[0] == null
                        ? DEFAULT_MIN_BUDGET
                        : budgetRange[0],
                      budgetRange[1] == null
                        ? DEFAULT_MAX_BUDGET
                        : budgetRange[1],
                    ]
                  }
                  stepValue={DEFAULT_STEP_VALUE}
                  onInput={handleSliderInput}
                  onThumbDragEnd={handleSliderDragEnd}
                />
              </div>
              <ul className="range-values-list rs-pb-37">
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={minError}
                    value={
                      budgetRange && budgetRange[0] === ""
                        ? ""
                        : thousandSeperatedValueFilter(budgetRange[0])
                    }
                    placeholder="0"
                    onChange={(e) => {
                      handleBudgetMinChange(e);
                    }}
                    onBlur={handleBudgetBlur}
                  ></TextBox>
                  <div className="error-msg"></div>
                </li>
                <li className="text-center">
                  <span className="middle-separator">-</span>
                </li>
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={maxError}
                    value={
                      budgetRange && budgetRange[1] === DEFAULT_MAX_BUDGET
                        ? "No max"
                        : thousandSeperatedValue(budgetRange[1])
                    }
                    placeholder="No max"
                    onChange={(e) => {
                      handleBudgetMaxChange(e);
                    }}
                    onBlur={handleBudgetBlur}
                  ></TextBox>
                </li>
              </ul>
            </div>
          </Accordion.Body>
        </Accordion.Item>
        {adType === "STR" && (
          <Accordion.Item eventKey="7">
            <Accordion.Header>Dates</Accordion.Header>
            <Accordion.Body className="pt-0">
              <DateRangeSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}
        <Accordion.Item eventKey="3">
          <Accordion.Header>
            {adType === "STR" ? "Rooms and beds" : "Beds and baths"}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="lp-btn-group-wrap">
              <label className="form-label">
                {adType === "STR" ? "Bedrooms" : "Beds"}
              </label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBedRooms()}
              </div>
            </div>

            {adType === "STR" && isPrivateRoomSelected && (
              <div className="lp-btn-group-wrap">
                <label className="form-label">Beds</label>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  {loadBeds()}
                </div>
              </div>
            )}

            <div className="lp-btn-group-wrap mb-3">
              <label className="form-label">Baths</label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBathRooms()}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {adType !== "STR" && (
          <Accordion.Item eventKey="4">
            <Accordion.Header>Square footage</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="slider-sec">
                <ul className="range-values-list list-pad-fix rs-pb-37">
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={minRef}>
                      <SelectDropDown
                        id={MIN_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMinList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        value={squareFootageMinValue}
                        onChange={(e) => handleSquareFootMinChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MIN_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                  <li className="text-center">
                    <span className="middle-separator">-</span>
                  </li>
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={maxRef}>
                      <SelectDropDown
                        id={MAX_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMaxList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        placeholder="Select"
                        value={squareFootageMaxValue}
                        onChange={(e) => handleSquareFootMaxChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MAX_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
        {adType === "STR" && (
          <Accordion.Item eventKey="6">
            <Accordion.Header>Additional requirements</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">
                {loadAdditionalRequirements()}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
        {!showMore && (
          <>
            <hr
              className="more-filters-divider"
              style={{
                margin: 0,
                borderTop: "1px solid rgb(207, 212, 221)",
                opacity: 1,
              }}
            />
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="reset-btn bg-transparent border-0 w-100 d-flex align-items-center justify-content-between py-3 px-0 more-filters-toggle"
              style={{ color: "var(--neutral-1000)" }}
            >
              <span>
                More{moreFilterCount > 0 ? ` (${moreFilterCount})` : ""}
              </span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
        {showMore && (
          <>
            <Accordion.Item eventKey="8">
              <Accordion.Header>
                Financing{" "}
                {getSelectedItems("financeType").length > 0 ||
                filterQuery?.preApproved
                  ? `(${
                      getSelectedItems("financeType").length +
                      (filterQuery?.preApproved ? 1 : 0)
                    })`
                  : ""}
              </Accordion.Header>
              <Accordion.Body className="pt-0">
                <div className="field-wrap mt-0 mb-0">
                  {loadFinance()}
                  <label className="d-block mb-2 form-check-label checkbox-label">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={filterQuery?.preApproved ? true : false}
                      onChange={handlePreApprovedClick}
                    />
                    Pre-approved for a loan
                  </label>
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="9">
              <Accordion.Header>
                Timeline{" "}
                {getSelectedItems("timeline").length > 0
                  ? `(${getSelectedItems("timeline").length})`
                  : ""}
              </Accordion.Header>
              <Accordion.Body className="pt-0">
                <div className="field-wrap mt-0 mb-0">{loadTimeline()}</div>
              </Accordion.Body>
            </Accordion.Item>
            {adType === BUY && (
              <Accordion.Item eventKey="10">
                <Accordion.Header>
                  Purchase type{" "}
                  {getSelectedItems("purchaseType").length > 0
                    ? `(${getSelectedItems("purchaseType").length})`
                    : ""}
                </Accordion.Header>
                <Accordion.Body className="pt-0">
                  <div className="field-wrap mt-0 mb-0">{loadPurchase()}</div>
                </Accordion.Body>
              </Accordion.Item>
            )}
          </>
        )}
      </Accordion>

      {props?.locationOnly ? null : (
      <div className="mt-2">
        <a role="button" className="reset-btn" onClick={() => clearFilter()}>
          Clear all
        </a>
      </div>
      )}
      </div>

      <PremiumPlanModal
        userDetails={userDetailsFromHook}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        updateReloadParam={updateReloadParamAfterPlanChange}
        fromInventoryListing={false}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};
export default LeftpanelTabContent;
